import type { APExam, UserAPCredit } from '@/app/types/interfaces';

export interface TranscriptEarnedCourse { baseId: string; status: 'completed'; grade: string; credits: number }

export interface TranscriptParseResult {
  earnedCourses: TranscriptEarnedCourse[];
  courseBaseIds: string[];
  apCredits: UserAPCredit[];
  warnings: string[];
}

// Never cross a PDF text line when identifying a course or its result.
const coursePattern = /\b(\d{2})[ \t:]+(\d{3})[ \t:]+(\d{3})\b/g;
const apMarker = /\b(?:CEEB\s+)?(?:ADVANCED\s+PLACEMENT(?:\s+IN)?|AP)(?:\s*:\s*|\s+)/i;
const passingGrades = new Set(['A', 'B+', 'B', 'C+', 'C', 'D', 'P', 'PA', 'PASS', 'S', 'CR']);
const unearnedGrades = new Set(['F', 'W', 'WF', 'WP', 'I', 'IN', 'INC', 'IP', 'NG', 'NC', 'NP', 'U', 'AU', 'AUDIT']);
const unearnedStatus = /\b(?:IN[ -]PROGRESS|WITHDRAWN|WITHDRAWAL|INCOMPLETE|FAILED|NOT\s+(?:COMPLETED|EARNED)|REGISTERED|ENROLLED|PLANNED|PENDING|DROPPED|CANCELLED|EXCLUDED|NO\s+CREDIT|AUDIT(?:ED)?)\b/i;
const nonCompletedHeading = /^(?:COURSES?\s+IN[ -]PROGRESS|IN[ -]PROGRESS(?:\s+COURSE(?:WORK|S))?|CURRENT\s+(?:ENROLLMENT|COURSES)|REGISTERED\s+COURSES|PLANNED\s+COURSES|FUTURE\s+COURSES)\b/i;
const academicHeading = /^(?:(?:FALL|SPRING|SUMMER|WINTER)\s+\d{4}\b|\d{4}\s+(?:FALL|SPRING|SUMMER|WINTER)\b|COMPLETED\s+COURSE(?:WORK|S)\b|ACADEMIC\s+(?:RECORD|HISTORY)\b|INSTITUTION(?:AL)?\s+CREDIT\b)/i;
const externalHeading = /^(?:EXTERNAL\s+EXAMINATIONS|(?:AP|ADVANCED\s+PLACEMENT|TRANSFER|EXAMINATION)\s+CREDITS?)\b/i;

function normalizeExamName(name: string): string {
  return name.toLowerCase().replace(/^ap\s+/, '').replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ').trim();
}

function readEarnedResult(row: string): { status: 'earned'; grade: string; credits: number } | { status: 'unearned' | 'uncertain' } {
  if (unearnedStatus.test(row)) return { status: 'unearned' };

  const labeledGrade = [...row.matchAll(/\bGRADE\s*[:=]\s*([A-Z]+\+?)(?![A-Z+-])/gi)];
  const labeledCredits = [...row.matchAll(/\b(?:EARNED\s+)?CREDITS?\s*[:=]\s*(\d+(?:\.\d+)?)(?![\d.])/gi)];
  let grade: string | undefined;
  let credits: number | undefined;

  if (labeledGrade.length || labeledCredits.length) {
    // Multiple credit columns or grades require human interpretation.
    if (labeledGrade.length !== 1 || labeledCredits.length !== 1) return { status: 'uncertain' };
    grade = labeledGrade[0][1].toUpperCase();
    credits = Number(labeledCredits[0][1]);
  } else {
    // Supported terminal columns: credits, grade, optional quality points;
    // or grade, credits. Other layouts fail closed rather than borrow a grade
    // from a title, another course, another line, or a term summary.
    const creditThenGrade = row.match(/(?:^|\s)(\d+(?:\.\d+)?)\s+([A-Z]+\+?)(?:\s+\d+(?:\.\d+)?)?\s*$/i);
    const gradeThenCredit = row.match(/(?:^|\s)([A-Z]+\+?)\s+(\d+(?:\.\d+)?)\s*$/i);
    const match = creditThenGrade ?? gradeThenCredit;
    if (!match) return { status: 'uncertain' };
    const prefix = row.slice(0, match.index).trim();
    if (!prefix || /\d(?:\.\d+)?$/.test(prefix)) return { status: 'uncertain' };
    grade = (creditThenGrade ? match[2] : match[1]).toUpperCase();
    credits = Number(creditThenGrade ? match[1] : match[2]);
  }

  if (unearnedGrades.has(grade) || credits <= 0) return { status: 'unearned' };
  return passingGrades.has(grade) && Number.isFinite(credits) ? { status: 'earned', grade, credits } : { status: 'uncertain' };
}

/**
 * Proposes candidates only. Parsing never saves data, guesses AP scores, or
 * treats the mere presence of a course identifier as proof of completion.
 * Unrecognized or wrapped layouts are intentionally left for manual review.
 */
export function parseTranscriptText(
  text: string,
  existingBaseIds: readonly string[],
  apExams: readonly APExam[],
): TranscriptParseResult {
  const warnings: string[] = [];
  if (!text.trim()) {
    return {
      courseBaseIds: [], earnedCourses: [], apCredits: [],
      warnings: ['No readable text was found. This PDF may be scanned; upload a text-based transcript or add courses and AP scores manually.'],
    };
  }

  const key = (value: string) => /^\d{2}[:\-]\d{3}[:\-]\d{3}$/.test(value) ? value.replace(/[:\-]/g, '') : value;
  const existing = new Set(existingBaseIds.map(key));
  const earned = new Map<string, TranscriptEarnedCourse | null>();
  let conflictingEarnedRows = 0;
  const apResults = new Map<string, { exam: APExam; score: number | null }>();
  let section: 'academic' | 'external' | 'in-progress' = 'academic';
  let uncertainRows = 0;
  let unearnedRows = 0;
  let externalRows = 0;
  let uncertainAPRows = 0;
  let conflictingAPScores = 0;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\u00a0/g, ' ').trim();
    if (!line) continue;
    const courseMatches = [...line.matchAll(coursePattern)];
    if (externalHeading.test(line)) {
      section = 'external';
      if (!courseMatches.length && !/\bSCORE\b/i.test(line)) continue;
    } else if (nonCompletedHeading.test(line) || (academicHeading.test(line) && unearnedStatus.test(line))) {
      section = 'in-progress';
    } else if (!courseMatches.length && academicHeading.test(line)) {
      section = 'academic';
    }

    const marker = line.match(apMarker);
    if (marker) {
      if (section === 'in-progress' || unearnedStatus.test(line)) {
        uncertainAPRows++;
        continue;
      }
      const details = line.slice(marker.index! + marker[0].length);
      const name = details.split(/\bSCORE\b|\b\d{2}[ \t:]+\d{3}[ \t:]+\d{3}\b|\b\d+(?:\.\d+)?\b/i)[0];
      const matches = apExams.filter(exam => normalizeExamName(exam.name) === normalizeExamName(name));
      const scores = [...details.matchAll(/\bSCORE\s*[:=]?\s*(\d+(?:\.\d+)?)(?![\d.])(?:\s*\/\s*(\d+))?/gi)];
      const score = scores.length === 1 ? Number(scores[0][1]) : NaN;
      if (matches.length !== 1 || !Number.isInteger(score) || score < 1 || score > 5 || (scores[0]?.[2] !== undefined && scores[0][2] !== '5')) {
        uncertainAPRows++;
        continue;
      }
      const exam = matches[0];
      const previous = apResults.get(exam.id);
      if (previous && previous.score !== score) {
        if (previous.score !== null) conflictingAPScores++;
        apResults.set(exam.id, { exam, score: null });
      } else if (!previous) {
        apResults.set(exam.id, { exam, score });
      }
      continue;
    }

    if (!courseMatches.length) continue;
    if (section === 'external') {
      externalRows++;
      continue;
    }
    if (section === 'in-progress' || unearnedStatus.test(line)) {
      unearnedRows++;
      continue;
    }
    if (courseMatches.length !== 1) {
      uncertainRows++;
      continue;
    }
    const match = courseMatches[0];
    const baseId = `${match[1]}${match[2]}${match[3]}`;
    const result = readEarnedResult(line.slice(match.index! + match[0].length));
    if (result.status === 'earned') {
      const prior = earned.get(baseId);
      if (prior === null) continue;
      if (prior && (prior.grade !== result.grade || prior.credits !== result.credits)) {
        earned.set(baseId, null); conflictingEarnedRows++;
      } else earned.set(baseId, { baseId, status: 'completed', grade: result.grade, credits: result.credits });
    } else if (result.status === 'unearned') unearnedRows++;
    else uncertainRows++;
  }

  const apCredits: UserAPCredit[] = [];
  for (const { exam, score } of apResults.values()) {
    if (score === null) continue;
    const rutgersEquivalents = exam.rutgersEquivalents.filter(equivalent =>
      score >= equivalent.minScore && score <= equivalent.maxScore,
    );
    if (!rutgersEquivalents.length) {
      uncertainAPRows++;
      continue;
    }
    apCredits.push({ examId: exam.id, examName: exam.name, score, rutgersEquivalents, isApplied: true });
  }

  if (uncertainRows) warnings.push(`${uncertainRows} course row(s) could not be confirmed as earned credit. Review and add them manually if appropriate.`);
  if (unearnedRows) warnings.push(`${unearnedRows} non-completed or non-credit course row(s) were excluded.`);
  if (externalRows) warnings.push(`${externalRows} external-exam or transfer row(s) were excluded from completed courses. Review their credit manually.`);
  if (uncertainAPRows) warnings.push(`${uncertainAPRows} AP record(s) need manual review: an exact exam name, explicit score, and matching credit equivalency are required. Scores are never guessed.`);
  if (conflictingAPScores) warnings.push(`${conflictingAPScores} AP exam(s) had conflicting scores and were excluded. Confirm the score manually.`);
  if (conflictingEarnedRows) warnings.push(`${conflictingEarnedRows} course(s) have conflicting earned grades or credits. Review their attempts manually.`);
  const earnedCourses = [...earned.values()].filter((row): row is TranscriptEarnedCourse => row !== null);
  return { courseBaseIds: earnedCourses.filter(row => !existing.has(row.baseId)).map(row => row.baseId), earnedCourses, apCredits, warnings };
}
